import { Injectable } from '@angular/core';
import { PluginListenerHandle } from '@capacitor/core';
import RfidReader from './rfid-reader/rfid-reader';
import { BehaviorSubject, Subject } from 'rxjs';
import { TRfidDevice } from '@monorepo/types';

export type TScannedRFIDTagInfo = {
  tagList: string[];
};

export type TRFIDConnectionInfo = {
  connectionState: 'CONNECTED' | 'DISCONNECTED';
  vendor: TRfidDevice['vendor'];
  deviceName: TRfidDevice['deviceName'];
  deviceId: string;
  readPower: number;
};

export type TScannedDevicesInfo = {
  deviceList: TRFIDNetworkDeviceInfo[];
};

export type TRFIDNetworkDeviceInfo = {
  hostname: string;
  ipAddress: string;
  macAddress: string;
};

export type TScanModes = 'single_tag' | 'multi_tag';

export type TReadpowerUpdateInfo = { oldReadpower: number; newReadpower: number };

@Injectable({
  providedIn: 'root',
})
export class RfidTagReaderService {
  // private rfidServiceID = '6e400001-b5a3-f393-e0a9-e50e24dcca9e';
  private rfidConnectionListner: PluginListenerHandle | undefined;
  private rfidDeviceScanListner: PluginListenerHandle | undefined;
  private mScanMode: TScanModes = 'multi_tag'; //single_tag or multi_tag
  private mReadPower = 500;
  private tagsList: string[] = [];
  // private deviceList: TRFIDNetworkDeviceInfo[] = [];
  private scannedRFIDTagsEmitter = new Subject<TScannedRFIDTagInfo>();
  private rfidConnectionStateEmitter = new BehaviorSubject<TRFIDConnectionInfo>({
    connectionState: 'DISCONNECTED',
    deviceId: '',
    deviceName: '',
    readPower: 500,
    vendor: 'CHAINWAY',
  });
  private readpowerUpdateEmitter = new BehaviorSubject<TReadpowerUpdateInfo>({
    oldReadpower: 0,
    newReadpower: 0,
  });
  // private rfidDeviceScanEmitter = new Subject<TScannedDevicesInfo>();
  // private networkDeviceDefaultPort = '8058'; // Default port for newland devices

  constructor() {
    this.startConnectionStateListner();
    this.startRfidTagReadListner();
    this.startReadpowerUpdateListener();
  }

  // async connectToNetworkBasedRfidDevice(
  //   networkDevice: TRFIDNetworkDeviceInfo,
  //   rfidScanMode: TScanModes = 'multi_tag',
  //   readPower: number = 500,
  // ) {
  //   // this.startConnectionStateListner();
  //   // this.startRfidTagReadListner();
  //   // this.rfidNetworkDeviceInfo = {
  //   //   hostname: '',
  //   //   ipAddress,
  //   //   macAddress: '',
  //   // };
  //   const ipAddressWithDefaultPort = networkDevice.ipAddress + ':' + this.networkDeviceDefaultPort;
  //   this.mScanMode = rfidScanMode;
  //   this.mReadPower = readPower;
  //   this.device = {
  //     connectionType: 'NETWORK',
  //     deviceDetails: networkDevice,
  //   };
  //   console.log('Trying to connect device with IP: ', networkDevice.ipAddress);
  //   console.log('Trying to connect device with PORT: ', this.networkDeviceDefaultPort);
  //   console.log({ deviceDetails: this.device.deviceDetails.ipAddress });
  //   RfidReader.connectToNetworkBasedRfidDevice({ ipAddress: ipAddressWithDefaultPort, readPower });
  // }

  // async scanRFIDDevicesOnNetwork() {
  //   console.log('scanRFIDDevicesOnNetwork called');
  //   RfidReader.scanRFIDDevicesOnNetwork();
  // }

  /**
   * @description helps in connecting to Vendor based RFID device.
   * @param deviceDetails - TRfidDevice
   * @param scanMode - TScanModes
   */
  connectToVendorBasedRfidDevice(deviceDetails: TRfidDevice, scanMode: TScanModes) {
    RfidReader.connectToVendorBasedRfidDevice({ ...deviceDetails, scanMode });
  }

  getScannedRFIDTagsEmitterSubscription() {
    return this.scannedRFIDTagsEmitter.asObservable();
  }

  getRFIDConnectionStateEmitterSubscription() {
    return this.rfidConnectionStateEmitter.asObservable();
  }

  getReadpowerUpdateEmitterSubscription() {
    return this.readpowerUpdateEmitter.asObservable();
  }

  /**
   * @description helps to start the tag read of Rfid Device.
   * returns status or error, if unable to read data.
   * @returns status => true / false
   */
  startRead() {
    console.log('start read called');
    RfidReader.startRFIDRead().then((res) => {
      if (res.status) {
        console.log('Started Rfid read');
      } else {
        console.log('Failed to start Rfid read');
      }
      return res.status;
    });
  }

  /**
   * @description helps to stop the tag read of Rfid Device.
   * returns status or error.
   * @returns status => true or false
   */
  stopRead() {
    console.log('stopRead called');
    RfidReader.stopRFIDRead().then((res) => {
      if (res.status) {
        console.log('stopped Rfid read');
      } else {
        console.log('Failed to stop Rfid read');
      }
      return res.status;
    });
  }

  /**
   * @description helps to update the Rfid scan mode from single to multi mode. By Default bulk scan mode
   * returns status or error, based on Mode toggle sucess of failure.
   * @param scanMode - single_tag or multi_tag.
   * @returns status => true | false (always returns true after update because we are managing scanning according to the modes instead of SDK)
   */
  updateScanMode(scanMode: TScanModes) {
    return RfidReader.updateScanMode({ scanMode: scanMode }).then((res) => {
      if (res.status) {
        this.mScanMode = scanMode;
        console.log('Rfid Mode updated success =>', this.mScanMode);
      } else {
        console.log('Rfid Mode updated failed');
      }
      return res.status;
    });
  }

  updateReadpower(readpower: number) {
    RfidReader.updateReadpower({ readpower });
  }

  /**
   * @description helps to disconnect from Rfid Device.
   * returns status or error, if unable to disconnect.
   * @returns status => true / false
   */
  disconnectDevice() {
    return RfidReader.disconnectRFIDDevice().then((res) => res);
  }

  private async startRfidTagReadListner() {
    await RfidReader.addListener('rfidTagDataUpdate', (info: { status: string; tagList: string[] }) => {
      console.log('Recieved Rfid Data Info', info);
      this.tagsList = info.tagList;
      this.scannedRFIDTagsEmitter.next({ tagList: info.tagList });
    });
  }

  private async startConnectionStateListner() {
    this.rfidConnectionListner = await RfidReader.addListener('rfidConnectionUpdate', (info: TRFIDConnectionInfo) => {
      console.log('Recieved Rfid Connection State =>', info.connectionState);
      this.rfidConnectionStateEmitter.next(info);
    });
  }

  private async startReadpowerUpdateListener() {
    await RfidReader.addListener('readpowerUpdate', (info: TReadpowerUpdateInfo) => {
      this.readpowerUpdateEmitter.next(info);
    });
  }

  // private async startRfidNetworkDeviceScanListener() {
  //   console.log('startRfidNetworkDeviceScanListener called');
  //   this.rfidDeviceScanListner = await RfidReader.addListener(
  //     'rfidDeviceNetworkScanUpdate',
  //     (info: { status: string; deviceList: TRFIDNetworkDeviceInfo[] }) => {
  //       console.log('Recieved Rfid Device scan Info');
  //       console.log('Recieved Rfid Device Info', info);
  //       console.log('Recieved Rfid Device list', info.deviceList);
  //       this.deviceList = info.deviceList;
  //       this.rfidDeviceScanEmitter.next({ deviceList: info.deviceList });
  //     },
  //   );
  // }
}
